<template>
    <section class="text-foxy-text" :aria-busy="previewReloadPending ? 'true' : 'false'">
        <div v-if="!hideHeader" class="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h3 class="text-xl font-bold text-foxy-text">
                Цікаві факти <span class="text-foxy-text-secondary">· {{ localTotalCount }}</span>
            </h3>
            <div class="flex flex-wrap items-center gap-3">
                <button v-if="allowCreate && isAuthenticated" @click="showAddForm = !showAddForm"
                    class="foxy-btn-primary !px-4 !py-2 !font-semibold">
                    <i class="fas fa-plus" aria-hidden="true"></i>
                    Додати факт
                </button>
                <a v-if="allUrl && localTotalCount > 4" :href="allUrl"
                    class="inline-flex items-center gap-2 rounded-lg text-sm font-semibold text-foxy-action transition-colors hover:text-foxy-action/80 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20">
                    Переглянути всі
                    <i class="fas fa-arrow-right text-xs" aria-hidden="true"></i>
                </a>
            </div>
        </div>

        <!-- Add Fact Form -->
        <div v-if="showAddForm"
            class="foxy-panel !rounded-xl mb-6 p-6">
            <h4 class="text-lg font-semibold text-foxy-text mb-4">Додати новий факт</h4>
            <form @submit.prevent="addFact">
                <div class="mb-4">
                    <textarea v-model="newFact" placeholder="Введіть цікавий факт про книгу..."
                        class="foxy-textarea !resize-none"
                        rows="3" required></textarea>
                </div>
                <div class="mb-4">
                    <label for="fact-source-url" class="mb-1 block text-sm font-semibold text-foxy-text">
                        Джерело <span class="font-normal text-foxy-text-secondary">(необов’язково)</span>
                    </label>
                    <input id="fact-source-url" v-model="newSourceUrl" type="url" maxlength="1024"
                        class="foxy-input w-full" placeholder="https://example.com/material">
                </div>
                <div class="flex space-x-3">
                    <button type="submit" :disabled="isSubmitting"
                        class="foxy-btn-primary !px-6 !py-2 !font-semibold">
                        <i class="fas fa-save"></i>
                        {{ isSubmitting ? 'Збереження...' : 'Зберегти' }}
                    </button>
                    <button type="button" @click="cancelAdd"
                        class="foxy-btn-secondary !px-6 !py-2 !font-semibold">
                        Скасувати
                    </button>
                </div>
            </form>
        </div>

        <p v-if="previewReloadError" role="status" class="mb-4 text-sm text-foxy-text-secondary">
            Не вдалося оновити список.
            <button type="button" class="font-semibold text-foxy-action hover:underline"
                :disabled="previewReloadPending" @click="refreshPreview">Спробувати ще раз</button>
        </p>
        <div class="flex flex-col justify-end mb-4" v-if="localFacts.length > 0" >
            <div class="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
            <fact-card v-for="fact in localFacts" :key="fact.id" :fact="fact" :book-slug="fact.book?.slug || bookSlug" :presentation-mode="presentationMode" :show-book-context="showBookContext"
                :current-user-id="currentUserId" @show-notification="showNotification" @like-toggled="handleLikeToggled"
                    @fact-deleted="handleFactDeleted" @fact-updated="handleFactUpdated">
                </fact-card>
            </div>
        </div>

        <div v-else class="text-center py-8">
            <svg class="w-16 h-16 mx-auto text-foxy-border mb-4" fill="none" stroke="currentColor"
                viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p class="text-foxy-text-secondary text-lg">
                Цікаві факти про цю книгу ще не додані
            </p>
        </div>
    </section>
</template>

<script>
import axios from 'axios';
import FactCard from './FactCard.vue';

export default {
    name: 'FactsList',
    components: {
        FactCard
    },
    props: {
        facts: {
            type: Array,
            default: () => []
        },
        bookSlug: {
            type: String,
            required: true
        },
        currentUserId: {
            type: Number,
            default: null
        },
        isModerator: {
            type: Boolean,
            default: false
        },
        hideHeader: {
            type: Boolean,
            default: false
        },
        presentationMode: { type: String, default: 'compact' },
        totalCount: { type: Number, default: null },
        allUrl: { type: String, default: '' },
        allowCreate: { type: Boolean, default: true },
        showBookContext: { type: Boolean, default: false }
    },
    data() {
        return {
            localFacts: this.facts.slice(0, 4),
            previewReloadPending: false,
            previewReloadError: false,
            previewReloadVersion: 0,
            localTotalCount: this.totalCount ?? this.facts.length,
            showAddForm: false,
            newFact: '',
            newSourceUrl: '',
            isSubmitting: false,
        };
    },
    computed: {
        isAuthenticated() {
            return this.currentUserId !== null;
        }
    },
    beforeDestroy() {
        this.invalidatePreviewReload();
    },
    watch: {
        totalCount(value) { this.localTotalCount = Math.max(0, value ?? this.localFacts.length); }
    },
    methods: {
        invalidatePreviewReload() {
            this.previewReloadVersion += 1;
            this.previewReloadPending = false;
            this.previewReloadError = false;
        },
        refillPreviewIfNeeded() {
            if (this.localFacts.length < Math.min(4, this.localTotalCount)) return this.refreshPreview();
        },
        async refreshPreview() {
            const version = ++this.previewReloadVersion;
            this.previewReloadPending = true;
            this.previewReloadError = false;
            try {
                // Reload the existing Show payload: archive APIs have different visibility contracts.
                const response = await axios.get(window.location.pathname + window.location.search, {
                    headers: { Accept: 'text/html' },
                });
                if (version !== this.previewReloadVersion) return;
                if (typeof response.data !== 'string') throw new Error('Invalid preview response');
                const page = new window.DOMParser().parseFromString(response.data, 'text/html');
                const lists = [...page.querySelectorAll('facts-list')]
                    .filter(node => node.getAttribute('book-slug') === this.bookSlug);
                if (lists.length !== 1) throw new Error('Preview affiliation unavailable');
                const rows = JSON.parse(lists[0].getAttribute(':facts'));
                const countValue = lists[0].getAttribute(':total-count');
                const total = Number(countValue);
                if (!countValue || !Number.isSafeInteger(total) || total < 0 || !Array.isArray(rows)
                    || rows.length !== Math.min(4, total)
                    || rows.some(row => !row || !Number.isSafeInteger(row.id) || typeof row.content !== 'string')
                    || new Set(rows.map(row => row.id)).size !== rows.length) throw new Error('Invalid preview payload');
                this.localFacts = rows;
                this.localTotalCount = total;
            } catch (error) {
                if (version === this.previewReloadVersion) this.previewReloadError = true;
            } finally {
                if (version === this.previewReloadVersion) this.previewReloadPending = false;
            }
        },

        async addFact() {
            if (!this.allowCreate || !this.bookSlug || this.isSubmitting || !this.newFact.trim()) return;

            this.isSubmitting = true;

            try {
                const response = await axios.post(`/books/${this.bookSlug}/facts`, {
                    content: this.newFact.trim(),
                    source_url: this.newSourceUrl.trim() || null,
                });

                if (response.data.success) {
                    this.handleFactAdded(response.data.fact);
                    this.newFact = '';
                    this.newSourceUrl = '';
                    this.showAddForm = false;
                    this.showNotification('Факт успішно додано!', 'success');
                } else {
                    this.showNotification(response.data.message || 'Помилка при додаванні факту.', 'error');
                }
            } catch (error) {
                console.error('Error adding fact:', error);

                // Пытаемся показать конкретное сообщение валидации (например, про мінімум 50 символів)
                let message = 'Помилка при додаванні факту.';
                if (error.response && error.response.status === 422 && error.response.data && error.response.data.errors) {
                    const errors = error.response.data.errors;
                    if (errors.content && errors.content.length) {
                        message = errors.content[0];
                    } else if (errors.source_url && errors.source_url.length) {
                        message = errors.source_url[0];
                    }
                }

                this.showNotification(message, 'error');
            }

            this.isSubmitting = false;
        },
        cancelAdd() {
            this.newFact = '';
            this.newSourceUrl = '';
            this.showAddForm = false;
        },
        handleFactAdded(fact) {
            if (!fact || fact.is_public === false || (fact.status && fact.status !== 'active')) return;
            if (this.localFacts.some(item => item.id === fact.id)) return;
            this.invalidatePreviewReload();
            this.localFacts = [fact, ...this.localFacts].slice(0, 4);
            this.localTotalCount += 1;
        },
        handleFactDeleted(factId) {
            if (!this.localFacts.some(fact => fact.id === factId)) return;
            this.invalidatePreviewReload();
            this.localTotalCount = Math.max(0, this.localTotalCount - 1);
            this.localFacts = this.localFacts.filter(fact => fact.id !== factId);
            return this.refillPreviewIfNeeded();
        },
        handleFactUpdated(updatedFact) {
            const index = this.localFacts.findIndex(f => f.id === updatedFact.id);
            if (index !== -1) {
                this.invalidatePreviewReload();
                this.$set(this.localFacts, index, updatedFact);
                return this.refillPreviewIfNeeded();
            }
        },
        handleLikeToggled(data) {
            const fact = this.localFacts.find(f => f.id === data.factId);
            if (fact) {
                this.invalidatePreviewReload();
                fact.is_liked_by_current_user = data.isLiked;
                fact.likes_count = data.likesCount;
                return this.refillPreviewIfNeeded();
            }
        },
        showNotification(message, type) {
            // Создаем уведомление
            const notification = document.createElement('div');
            notification.className = `fixed top-4 right-4 z-50 px-6 py-3 rounded-xl border font-semibold shadow-lg transform transition-all duration-300 ${type === 'success' ? 'bg-foxy-success/10 border-foxy-success/30 text-foxy-success' : type === 'error' ? 'bg-foxy-error/10 border-foxy-error/30 text-foxy-error' : 'bg-foxy-action/10 border-foxy-action/30 text-foxy-action'
                }`;
            notification.textContent = message;

            document.body.appendChild(notification);

            // Показываем уведомление
            setTimeout(() => {
                notification.style.transform = 'translateX(0)';
                notification.style.opacity = '1';
            }, 100);

            // Убираем уведомление через 3 секунды
            setTimeout(() => {
                notification.style.transform = 'translateX(100%)';
                notification.style.opacity = '0';
                setTimeout(() => {
                    if (notification.parentNode) {
                        notification.parentNode.removeChild(notification);
                    }
                }, 300);
            }, 3000);
        }
    }
};
</script>
